import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { MapContainer, TileLayer, GeoJSON, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchCountryData } from './App';

const COLORS = [
  '#1976d2', '#ba68c8', '#ffd54f', '#4db6ac', '#ff8a65', '#90caf9', '#a5d6a7', '#f06292', '#fff176', '#81c784', '#e57373', '#64b5f6', '#dce775', '#ffd740', '#b0bec5', '#fbc02d', '#388e3c', '#0288d1', '#c2185b', '#7b1fa2', '#512da8', '#303f9f', '#0097a7', '#00796b', '#689f38', '#afb42b', '#fbc02d', '#e64a19', '#5d4037', '#616161', '#455a64'
];

function getColorByVolume(volume, maxVolume) {
  if (!maxVolume) return '#e0e0e0';
  const percent = volume / maxVolume;
  if (percent > 0.75) return '#1976d2';
  if (percent > 0.38) return '#64b5f6';
  if (percent > 0.17) return '#90caf9';
  if (percent > 0.007) return '#b3e5fc';
  if (percent > 0) return '#e1f5fe';
  return '#e0e0e0';
}

// Helper to map country names to ISO_A2 codes if needed
const countryNameToIso = {
  'UNITED ARAB EMIRATES': 'AE',
  'SAUDI ARABIA': 'SA',
  'IRAN': 'IR',
  // Add more as needed if d.Country returns full names (e.g., 'UNITED STATES': 'US', 'RUSSIA': 'RU', 'CHINA': 'CN', 'INDIA': 'IN')
  'UNITED STATES': 'US',
  'RUSSIA': 'RU',
  'CHINA': 'CN',
  'INDIA': 'IN',
  'ISRAEL': 'IL', // Assuming Israel is also a full name sometimes, or just to be safe
};

export default function WorldMapPage({ countryData: initialCountryData, query: initialQuery, setQuery, handleSearch, loading, startDate, endDate, actualQuery }) {
  const [countryData, setCountryData] = useState(initialCountryData || []);
  const [query, setLocalQuery] = useState(initialQuery || 'UAE');
  const [maxVolume, setMaxVolume] = useState(0);
  const [pieData, setPieData] = useState({});
  const [mapData, setMapData] = useState([]);
  const [error, setError] = useState(null);
  const [geoJson, setGeoJson] = useState(null);
  const [topCountryCodes, setTopCountryCodes] = useState([]);
  const [topCountryColors, setTopCountryColors] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        setError(null);
        const params = {
          StartDate: startDate,
          EndDate: endDate,
          Query: actualQuery,
        };
        const data = await fetchCountryData(actualQuery, params);
        console.log('Fetched Country Data for WorldMapPage:', data);
        setCountryData(data);
        const volumes = data.map(d => d.Volume);
        const currentMaxVolume = volumes.length > 0 ? Math.max(...volumes) : 0;
        setMaxVolume(currentMaxVolume);
        setMapData(data);
        // Sort by volume descending and pick top 5
        const sorted = [...data].sort((a, b) => b.Volume - a.Volume);
        const topCountries = sorted.slice(0, 5);
        const otherVolume = sorted.slice(5).reduce((sum, d) => sum + d.Volume, 0);
        const pieLabels = topCountries.map(d => d.CountryName || d.Country).concat('Other');
        const pieValues = topCountries.map(d => d.Volume).concat(otherVolume);

        // Create the country-color mapping
        const newTopCountryColors = {};
        const newTopCountryCodes = [];

        topCountries.forEach((d, index) => {
            const countryCode = (d.Country || '').toUpperCase(); // This could be 'US', 'AE', or 'UNITED ARAB EMIRATES'
            // Attempt to get ISO code if d.Country is a name
            const isoCode = countryNameToIso[countryCode] || countryCode;
            newTopCountryColors[isoCode] = COLORS[index];
            newTopCountryCodes.push(isoCode);
        });
        setTopCountryColors(newTopCountryColors);
        setTopCountryCodes(newTopCountryCodes);

        setPieData({
          labels: pieLabels,
          datasets: [{
            data: pieValues,
            backgroundColor: COLORS.slice(0, pieLabels.length),
            borderWidth: 1
          }]
        });
        // Store top country codes (uppercase) for highlighting
        // const newTopCountryCodes = topCountries.map(d => (d.Country || '').toUpperCase()); // This line moved and modified
        // setTopCountryCodes(newTopCountryCodes); // This line moved and modified

        console.log('WorldMapPage - maxVolume:', currentMaxVolume);
        console.log('WorldMapPage - topCountryCodes:', newTopCountryCodes);
        console.log('WorldMapPage - topCountryColors:', newTopCountryColors);

      } catch (err) {
        setError(err.message || 'Failed to load country data');
        console.error('Error fetching country data for WorldMapPage:', err);
        setCountryData([]);
        setPieData({});
        setTopCountryCodes([]);
        setTopCountryColors({});
      }
    }
    fetchData();
  }, [actualQuery, startDate, endDate]);

  // Map country code to volume
  const countryVolumeMap = {};
  mapData.forEach(d => {
    // Ensure consistent key for the map: use ISO code if available
    const countryCode = (d.Country || '').toUpperCase();
    const isoCode = countryNameToIso[countryCode] || countryCode;
    countryVolumeMap[isoCode] = d.Volume;
  });

  // Fetch GeoJSON for world countries
  useEffect(() => {
    fetch('https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson')
      .then(res => res.json())
      .then(setGeoJson)
      .catch((err) => {
        console.error('Error fetching GeoJSON:', err);
        setGeoJson(null);
      });
  }, []);

  return (
    <div className="world-map-page" style={{ padding: '2rem' }}>
      {/* Remove the topbar search bar here, since query is now controlled by parent */}
      {error && <div style={{ color: 'red', margin: '1rem 0' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '2rem', marginTop: '2rem' }}>
        <div style={{ flex: 1, minWidth: 350 }}>
          <h3>Share Of Countries/Region</h3>
          {pieData.labels ? (
            <Pie data={pieData} options={{ plugins: { legend: { position: 'bottom' } } }} />
          ) : (
            <p>No data for pie chart.</p>
          )}
        </div>
        <div style={{ flex: 2, minWidth: 500 }}>
          <h3>World Map</h3>
          <MapContainer style={{ height: '400px', width: '100%' }} center={[20, 0]} zoom={2} scrollWheelZoom={true}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            {geoJson && (
              <GeoJSON
                data={geoJson}
                style={feature => {
                  const code = (feature.properties.ISO_A2 || '').toUpperCase();
                  console.log('GeoJSON feature code:', code);
                  const volume = countryVolumeMap[code] || 0;
                  const isTop = topCountryCodes.includes(code);

                  // Determine fill color
                  let fillColor;
                  if (isTop && topCountryColors[code]) {
                      fillColor = topCountryColors[code]; // Use pie chart color for top countries
                  } else {
                      fillColor = getColorByVolume(volume, maxVolume); // Fallback to volume-based color
                  }

                  console.log(`Country: ${code}, Volume: ${volume}, isTop: ${isTop}, fillColor: ${fillColor}`);

                  return {
                    fillColor: fillColor,
                    weight: isTop ? 4 : 1,
                    color: isTop ? '#FFD700' : '#333',
                    fillOpacity: 0.85
                  };
                }}
                onEachFeature={(feature, layer) => {
                  const code = feature.properties.ISO_A2;
                  const volume = countryVolumeMap[code] || 0;
                  layer.bindTooltip(
                    `<strong>${feature.properties.ADMIN}</strong><br/>Volume: ${volume.toLocaleString()}`,
                    { sticky: true }
                  );
                }}
              />
            )}
          </MapContainer>
          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ width: 20, height: 20, background: '#1976d2', display: 'inline-block', borderRadius: 3 }}></span> 75.4%+
            <span style={{ width: 20, height: 20, background: '#64b5f6', display: 'inline-block', borderRadius: 3 }}></span> 24.3-38.3%
            <span style={{ width: 20, height: 20, background: '#90caf9', display: 'inline-block', borderRadius: 3 }}></span> 1.7-17%
            <span style={{ width: 20, height: 20, background: '#b3e5fc', display: 'inline-block', borderRadius: 3 }}></span> 0.9-1.7%
            <span style={{ width: 20, height: 20, background: '#e1f5fe', display: 'inline-block', borderRadius: 3 }}></span> 0-0.7%
            <span style={{ width: 20, height: 20, background: '#e0e0e0', display: 'inline-block', borderRadius: 3 }}></span> No Result
          </div>
        </div>
      </div>
    </div>
  );
} 